import { useState, useCallback, useEffect } from 'react';
import { ImageIcon, Download, Upload, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

interface CompressedImage {
    original: File;
    compressed: Blob | null;
    originalSize: number;
    compressedSize: number;
    preview: string;
    compressedPreview: string;
}

const ImageCompressor = () => {
    const [images, setImages] = useState<CompressedImage[]>([]);
    const [quality, setQuality] = useState([80]);
    const [isCompressing, setIsCompressing] = useState(false);

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const compressImage = useCallback(async (file: File, quality: number): Promise<{ blob: Blob; preview: string }> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx?.drawImage(img, 0, 0);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve({ blob, preview: URL.createObjectURL(blob) });
                        } else {
                            reject(new Error('Compression failed'));
                        }
                    },
                    'image/jpeg',
                    quality / 100
                );
            };

            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = URL.createObjectURL(file);
        });
    }, []);

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsCompressing(true);

        try {
            const newImages: CompressedImage[] = [];

            for (const file of files) {
                if (!file.type.startsWith('image/')) {
                    toast.error(`${file.name} is not an image`);
                    continue;
                }

                const { blob, preview } = await compressImage(file, quality[0]);

                newImages.push({
                    original: file,
                    compressed: blob,
                    originalSize: file.size,
                    compressedSize: blob.size,
                    preview: URL.createObjectURL(file),
                    compressedPreview: preview,
                });
            }

            setImages(prev => [...prev, ...newImages]);
            toast.success(`Compressed ${newImages.length} image(s)`);
        } catch (error) {
            toast.error('Compression failed');
        } finally {
            setIsCompressing(false);
        }
    }, [quality, compressImage]);

    const recompressAll = useCallback(async () => {
        if (images.length === 0) return;

        setIsCompressing(true);

        try {
            const updated = await Promise.all(
                images.map(async (img) => {
                    const { blob, preview } = await compressImage(img.original, quality[0]);
                    return {
                        ...img,
                        compressed: blob,
                        compressedSize: blob.size,
                        compressedPreview: preview,
                    };
                })
            );

            setImages(updated);
            toast.success('Recompressed all images');
        } catch (error) {
            toast.error('Recompression failed');
        } finally {
            setIsCompressing(false);
        }
    }, [images, quality, compressImage]);

    const downloadImage = (img: CompressedImage) => {
        if (!img.compressed) return;

        const a = document.createElement('a');
        a.href = img.compressedPreview;
        a.download = `compressed_${img.original.name.replace(/\.[^/.]+$/, '')}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Downloaded!');
    };

    const downloadAll = () => {
        images.forEach((img, i) => {
            setTimeout(() => downloadImage(img), i * 200);
        });
    };

    const removeImage = (index: number) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const clearAll = () => {
        setImages([]);
        toast.success('Cleared all images');
    };

    const totalSaved = images.reduce((acc, img) => acc + (img.originalSize - img.compressedSize), 0);

    // Cleanup Object URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            images.forEach(img => {
                if (img.preview) URL.revokeObjectURL(img.preview);
                if (img.compressedPreview) URL.revokeObjectURL(img.compressedPreview);
            });
        };
    }, [images]);

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <ImageIcon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Image Compressor</h1>
                        <p className="text-muted-foreground">Compress images without losing quality</p>
                    </div>
                </div>
            </div>

            {/* Upload & Controls */}
            <Card className="mb-6">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                        {/* Upload */}
                        <label className="flex-1 w-full">
                            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                <p className="font-medium">Drop images here or click to upload</p>
                                <p className="text-sm text-muted-foreground mt-1">PNG, JPG, WebP supported</p>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                        </label>

                        {/* Quality Slider */}
                        <div className="w-full md:w-64 space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">Quality</span>
                                <span className="text-sm font-bold text-primary">{quality[0]}%</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <ZoomOut className="h-4 w-4 text-muted-foreground" />
                                <Slider
                                    value={quality}
                                    onValueChange={setQuality}
                                    min={10}
                                    max={100}
                                    step={5}
                                    className="flex-1"
                                />
                                <ZoomIn className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <Button
                                onClick={recompressAll}
                                variant="outline"
                                className="w-full"
                                disabled={images.length === 0 || isCompressing}
                            >
                                {isCompressing ? 'Compressing...' : 'Recompress All'}
                            </Button>
                        </div>
                    </div>

                    {/* Stats */}
                    {images.length > 0 && (
                        <div className="mt-6 pt-6 border-t flex flex-wrap gap-6 justify-between items-center">
                            <div className="flex gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground">Images</p>
                                    <p className="text-2xl font-bold">{images.length}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Space Saved</p>
                                    <p className="text-2xl font-bold text-green-500">{formatSize(totalSaved)}</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Button onClick={downloadAll} disabled={images.length === 0}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download All
                                </Button>
                                <Button variant="destructive" onClick={clearAll}>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Clear
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Image Grid */}
            {images.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {images.map((img, index) => {
                        const savings = ((img.originalSize - img.compressedSize) / img.originalSize * 100).toFixed(0);

                        return (
                            <Card key={index} className="overflow-hidden">
                                <div className="aspect-video relative bg-muted">
                                    <img
                                        src={img.compressedPreview}
                                        alt={img.original.name}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                                        -{savings}%
                                    </div>
                                </div>
                                <CardContent className="p-4">
                                    <p className="font-medium truncate mb-2">{img.original.name}</p>
                                    <div className="flex justify-between text-sm text-muted-foreground mb-3">
                                        <span>{formatSize(img.originalSize)} → {formatSize(img.compressedSize)}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button size="sm" className="flex-1" onClick={() => downloadImage(img)}>
                                            <Download className="h-3 w-3 mr-1" />
                                            Download
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => removeImage(index)}>
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ImageCompressor;
